/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	forwardRef,
	type ForwardRefExoticComponent,
	type ReactNode,
	type RefAttributes,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';

import { css, jsx, keyframes } from '@compiled/react';

import { exitingDurations } from '@atlaskit/motion/exiting-durations';
import { token } from '@atlaskit/tokens';

import { RovoThinkingPill, type RovoThinkingPillProps } from './RovoThinkingPill';

const centerPillStyles = css({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

enum ThinkingView {
	Pill = 'pill',
	Full = 'full',
}

// Delay before a view swap, as a plain number for `setTimeout`. Sourced from
// `@atlaskit/motion`'s numeric exit duration (175ms) rather than the ADS motion
// tokens (which are CSS values, not numbers). It doesn't exactly equal the
// token-based exit `animationDuration` (`motion.duration.short`, 150ms) used in
// the CSS, and that's fine: the timer only needs to be >= the exit animation so
// the outgoing side finishes fading before it's hidden.
const TRANSITION_MS = exitingDurations.medium;

const pillFadeInKeyframes = keyframes({
	from: { opacity: 0 },
	to: { opacity: 1 },
});

const pillFadeOutKeyframes = keyframes({
	from: { opacity: 1 },
	to: { opacity: 0 },
});

const fullSlideInKeyframes = keyframes({
	from: { opacity: 0, transform: 'translateY(100%)' },
	to: { opacity: 1, transform: 'translateY(0)' },
});

const fullSlideOutKeyframes = keyframes({
	from: { opacity: 1, transform: 'translateY(0)' },
	to: { opacity: 0, transform: 'translateY(100%)' },
});

const animationBaseStyles = css({
	animationFillMode: 'both',
});

const pillEnterStyles = css({
	animationName: pillFadeInKeyframes,
	animationDuration: token('motion.duration.short'),
	animationTimingFunction: token('motion.easing.out.practical'),
	// Hold the pill invisible for a short beat after the full view has slid out
	// before fading it in, so the two don't feel glued together. `both` fill mode
	// (from `animationBaseStyles`) keeps it at opacity 0 during this delay.
	animationDelay: token('motion.duration.short'),
});
const pillExitStyles = css({
	animationName: pillFadeOutKeyframes,
	animationDuration: token('motion.duration.short'),
	animationTimingFunction: token('motion.easing.in.practical'),
});

const fullEnterStyles = css({
	animationName: fullSlideInKeyframes,
	animationDuration: token('motion.duration.medium'),
	animationTimingFunction: token('motion.easing.out.bold'),
});
const fullExitStyles = css({
	animationName: fullSlideOutKeyframes,
	animationDuration: token('motion.duration.medium'),
	animationTimingFunction: token('motion.easing.in.practical'),
});

const reducedMotionStyles = css({
	'@media (prefers-reduced-motion: reduce)': {
		animationName: 'none',
	},
});

/** Default delay before the expanded experience auto-minimizes into the pill. */
const DEFAULT_AUTO_MINIMIZE_DELAY_MS = 8000;
/**
 * Minimum remaining budget (ms) granted after the pointer/focus leaves, so a
 * brief hover near the end of the countdown doesn't immediately trigger
 * auto-minimize.
 */
const MIN_REMAINING_MS = 2000;

/**
 * Imperative handle for triggering a minimize/expand from outside the
 * component (e.g. a "Hide" button elsewhere in your UI), without a
 * controlled prop + callback contract.
 */
export type RovoThinkingMinimizationHandle = {
	/** Forces the pill to show. */
	minimize: () => void;
	/** Forces the full experience (`children`) to show, and resets the auto-minimize countdown. */
	expand: () => void;
};

export type RovoThinkingMinimizationProps = {
	/**
	 * The full (non-minimized) thinking experience, e.g. a
	 * `RovoThinkingBarWrapper` around a `RovoThinkingBar` or a
	 * `RovoThinkingReadyTransition`. Rendered while not minimized.
	 */
	children: ReactNode;
	/**
	 * Props forwarded to the `RovoThinkingPill` shown while minimized.
	 */
	pillProps?: Omit<RovoThinkingPillProps, 'onClick'>;
	/**
	 * Delay in milliseconds before the expanded experience automatically
	 * minimizes into the pill. Defaults to `8000` (8s). Pass `null` (or a
	 * value `<= 0`) to disable auto-minimize.
	 *
	 * The countdown pauses while the pointer is hovering / focused
	 */
	autoMinimizeDelayMs?: number | null;
	/**
	 * When `false`, minimization is disabled: the pill and the auto-minimize
	 * countdown are switched off and `children` are always shown (still inside the
	 * entrance/exit transition, so toggling this animates between the thinking and
	 * non-thinking experiences). Defaults to `true`.
	 */
	isThinking?: boolean;
	testId?: string;
};

export const RovoThinkingMinimization: ForwardRefExoticComponent<
	RovoThinkingMinimizationProps & RefAttributes<RovoThinkingMinimizationHandle>
> = forwardRef<RovoThinkingMinimizationHandle, RovoThinkingMinimizationProps>(
	function RovoThinkingMinimization(
		{
			children,
			pillProps,
			autoMinimizeDelayMs = DEFAULT_AUTO_MINIMIZE_DELAY_MS,
			isThinking = true,
			testId,
		},
		ref,
	) {
		const [isMinimized, setIsMinimized] = useState(false);

		// The side currently occupying the layout. It lags behind the `showPill`
		// target during a swap so the outgoing side can finish its exit animation
		// (see the swap effect below) before the incoming side takes over.
		const [displayedView, setDisplayedView] = useState<ThinkingView>(ThinkingView.Full);

		// `null` means auto-minimize is disabled; otherwise this *is* the delay
		// to use — one variable instead of a separate "is configured" flag plus
		// a redundantly-defaulted number.
		const configuredDelayMs =
			autoMinimizeDelayMs != null && autoMinimizeDelayMs > 0 ? autoMinimizeDelayMs : null;

		const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
		const scheduledAtRef = useRef<number | null>(null);
		const remainingRef = useRef(0);

		const clearTimer = useCallback(() => {
			if (timeoutIdRef.current !== null) {
				clearTimeout(timeoutIdRef.current);
				timeoutIdRef.current = null;
			}
			scheduledAtRef.current = null;
		}, []);

		const startTimer = useCallback(
			(delay: number) => {
				clearTimer();
				scheduledAtRef.current = Date.now() + delay;
				timeoutIdRef.current = setTimeout(() => {
					timeoutIdRef.current = null;
					scheduledAtRef.current = null;
					setIsMinimized(true);
				}, delay);
			},
			[clearTimer],
		);

		// Starts a fresh, full-length countdown for every expanded session
		// (including on mount), and stops it once minimized.
		useEffect(() => {
			if (!isThinking || isMinimized || configuredDelayMs === null) {
				clearTimer();
				return;
			}
			remainingRef.current = configuredDelayMs;
			startTimer(configuredDelayMs);
			return clearTimer;
		}, [isMinimized, isThinking, configuredDelayMs, startTimer, clearTimer]);

		const handlePauseTimer = useCallback(() => {
			if (scheduledAtRef.current === null) {
				return; // not currently running
			}
			remainingRef.current = Math.max(0, scheduledAtRef.current - Date.now());
			clearTimer();
		}, [clearTimer]);

		const handleResumeTimer = useCallback(() => {
			if (isMinimized || configuredDelayMs === null || timeoutIdRef.current !== null) {
				return; // already minimized, disabled, or already running
			}
			const minFloor = Math.min(MIN_REMAINING_MS, configuredDelayMs);
			startTimer(Math.max(remainingRef.current, minFloor));
		}, [isMinimized, configuredDelayMs, startTimer]);

		const handleExpandFromPill = useCallback(() => setIsMinimized(false), []);

		useImperativeHandle(
			ref,
			() => ({
				minimize: () => setIsMinimized(true),
				expand: () => setIsMinimized(false),
			}),
			[],
		);

		// When thinking stops, drop any lingering minimized state so the next
		// thinking session starts from the full experience rather than the pill.
		// Done during render (React re-renders before committing) to avoid the
		// extra commit + effect pass an effect-based reset would trigger. The
		// guard keeps it from looping.
		if (!isThinking && isMinimized) {
			setIsMinimized(false);
		}

		const showPill = isMinimized && isThinking;
		const target: ThinkingView = showPill ? ThinkingView.Pill : ThinkingView.Full;

		/**
		 * We have to use our own state to track transitions as ExitingPersistence won't keep the
		 * children's state (e.g. progress bars)
		 */
		useEffect(() => {
			if (displayedView === target) {
				return;
			}
			const swapId = setTimeout(() => setDisplayedView(target), TRANSITION_MS);
			return () => clearTimeout(swapId);
		}, [target, displayedView]);

		const pillShown = displayedView === ThinkingView.Pill;
		// The pill is exiting while it's still displayed but the target has moved on.
		const pillExiting = pillShown && target === ThinkingView.Full;
		// The full view is exiting while it's still displayed but headed to the pill.
		const fullExiting = !pillShown && target === ThinkingView.Pill;

		return (
			<div
				data-testid={testId ?? 'rovo-thinking-minimization'}
				onMouseEnter={configuredDelayMs !== null && isThinking ? handlePauseTimer : undefined}
				onMouseLeave={configuredDelayMs !== null && isThinking ? handleResumeTimer : undefined}
				onFocus={configuredDelayMs !== null && isThinking ? handlePauseTimer : undefined}
				onBlur={configuredDelayMs !== null && isThinking ? handleResumeTimer : undefined}
			>
				{pillShown ? (
					<span
						css={[
							centerPillStyles,
							animationBaseStyles,
							pillExiting && pillExitStyles,
							!pillExiting && pillEnterStyles,
							reducedMotionStyles,
						]}
					>
						<RovoThinkingPill {...pillProps} onClick={handleExpandFromPill} />
					</span>
				) : null}
				{/*
				 * Keep the full experience mounted at all times and just hide it once
				 * the pill has fully taken over, so any state it owns (e.g. the
				 * progress-bar) persists
				 */}
				<div
					hidden={pillShown}
					css={[
						!pillShown && animationBaseStyles,
						!pillShown && fullExiting && fullExitStyles,
						!pillShown && !fullExiting && fullEnterStyles,
						!pillShown && reducedMotionStyles,
					]}
				>
					{children}
				</div>
			</div>
		);
	},
);
